import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import { formatMoney } from '../../utils/format';
import api from '../../api';
import { CountUp, useSecondsLeft, fmtClock, slotOnClock, teamLogo, useFitWidth } from './poolFx';

// Pools home: tabs (My pools / Open / History) of big, same-size cards.
export default function PoolsList({ userId, onSelect, onCreate, onOpenQueues, lang = 'en' }) {
  const t = lang === 'fr' ? TR.fr : TR.en;
  const [pools, setPools] = useState([]);
  const [membersByPool, setMembersByPool] = useState({});
  const [myTeamByPool, setMyTeamByPool] = useState({});   // pool_id -> { abbrev, name } (my drafted NHL team)
  const [myStandings, setMyStandings] = useState({});     // pool_id -> { rank, score, of }
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(null);
  const fitRef = useFitWidth(1200);                       // laptops: scale as one piece                   // null until we know which tab makes sense

  const fetchPools = useCallback(async () => {
    const { data: poolRows, error } = await supabase
      .from('pools').select('*').order('created_at', { ascending: false });
    if (error) { setPools([]); setLoading(false); return; }

    const ids = (poolRows || []).map((p) => p.id);
    const byPool = {};
    if (ids.length) {
      const { data: mem } = await supabase
        .from('pool_members')
        .select('pool_id, user_id, draft_slot, profiles(username)')
        .in('pool_id', ids);
      (mem || []).forEach((m) => { (byPool[m.pool_id] = byPool[m.pool_id] || []).push(m); });
    }
    // my drafted NHL team in each pool (it's my identity there)
    const { data: myTeams } = await supabase
      .from('pool_picks').select('pool_id, team, player_name, position')
      .eq('user_id', userId).eq('position', 'T');
    const teams = {};
    (myTeams || []).forEach((p) => { teams[p.pool_id] = { abbrev: p.team, name: p.player_name }; });

    setMyTeamByPool(teams);
    setMembersByPool(byPool);
    setPools(poolRows || []);
    setLoading(false);
  }, [userId]);

  useEffect(() => { fetchPools(); }, [fetchPools]);

  useEffect(() => {
    const channel = supabase
      .channel('pools-list')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pools' }, fetchPools)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pool_members' }, fetchPools)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchPools]);

  const isMine = useCallback(
    (p) => (membersByPool[p.id] || []).some((m) => m.user_id === userId),
    [membersByPool, userId]
  );

  const ORDER = { drafting: 0, active: 1, lobby: 2 };
  const mine = pools.filter((p) => isMine(p) && p.status !== 'completed')
    .sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9));
  const open = pools.filter((p) => p.status === 'lobby' && !isMine(p));
  const history = pools.filter((p) => p.status === 'completed')
    .sort((a, b) => Number(isMine(b)) - Number(isMine(a)));

  // start on "My pools" when you have any, otherwise "Open"
  const activeTab = tab ?? (mine.length ? 'mine' : 'open');

  // my rank in active pools
  const activeKey = mine.filter((p) => p.status === 'active').map((p) => p.id).join(',');
  useEffect(() => {
    if (!activeKey) return undefined;
    let alive = true;
    activeKey.split(',').forEach(async (id) => {
      try {
        const s = await api.fetchPoolStandings(id);
        const rows = s?.standings || [];
        const me = rows.find((r) => r.user_id === userId);
        if (alive && me) setMyStandings((prev) => ({ ...prev, [id]: { rank: me.rank, score: me.score, of: rows.length } }));
      } catch (e) { /* card just shows without rank */ }
    });
    return () => { alive = false; };
  }, [activeKey, userId]);

  const list = activeTab === 'mine' ? mine : activeTab === 'open' ? open : history;

  return (
    <div ref={fitRef} className="pools-container pools-home">
      <div className="pools-header">
        <h2>{t.title}</h2>
        <div className="pools-header-actions">
          <button className="pool-btn-cancel" onClick={onOpenQueues}>{t.myQueues}</button>
          <button className="pools-create-btn" onClick={onCreate}>+ {t.create}</button>
        </div>
      </div>

      <div className="ph-tabs" role="tablist">
        {[['mine', t.tabMine, mine.length], ['open', t.tabOpen, open.length], ['history', t.tabHistory, history.length]].map(([k, label, count]) => (
          <button key={k} role="tab" aria-selected={activeTab === k} className={activeTab === k ? 'active' : ''} onClick={() => setTab(k)}>
            {label}{count ? <span className="ph-tab-count">{count}</span> : null}
          </button>
        ))}
      </div>

      {loading && <div className="pools-empty">{t.loading}</div>}

      {!loading && list.length === 0 && (
        <div className="ph-empty">
          <span>{activeTab === 'mine' ? t.emptyMine : activeTab === 'open' ? t.emptyOpen : t.emptyHistory}</span>
          {activeTab !== 'history' && <button className="pools-create-btn" onClick={onCreate}>+ {t.create}</button>}
        </div>
      )}

      {!loading && list.length > 0 && (
        <div className="ph-grid">
          {list.map((p, i) => (
            <PoolCard
              key={p.id}
              pool={p}
              index={i}
              members={membersByPool[p.id] || []}
              mine={isMine(p)}
              myTeam={myTeamByPool[p.id]}
              standing={myStandings[p.id]}
              userId={userId}
              onSelect={onSelect}
              t={t}
              lang={lang}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// One card, same size for every pool and status.
function PoolCard({ pool, index, members, mine, myTeam, standing, userId, onSelect, t, lang }) {
  const secs = useSecondsLeft(pool.status === 'drafting' ? pool.pick_deadline : null);
  const n = members.length;
  const slot = pool.status === 'drafting' ? slotOnClock(pool.current_pick_index, n, !!pool.team_round) : null;
  const onClock = members.find((m) => m.draft_slot === slot);
  const myTurn = !!onClock && onClock.user_id === userId;
  const winner = members.find((m) => m.user_id === pool.winner_id);
  const iWon = pool.status === 'completed' && pool.winner_id === userId;
  const format = pool.format === 'playoff' ? t.formatPlayoff : t.formatRegular;

  let stat = null;
  let action = t.open;
  if (pool.status === 'drafting') {
    stat = (
      <div className="ph-card-stat">
        <span className="ph-card-label">{myTurn ? t.youreUp : t.onClock}</span>
        <span className="ph-card-big">
          <span className="ph-card-big-text">{myTurn ? t.makeYourPick : (onClock?.profiles?.username || '—')}</span>
          {secs != null && <span className={`ph-card-clock ${secs <= 10 ? 'urgent' : ''}`}>{fmtClock(secs)}</span>}
        </span>
      </div>
    );
    action = t.enterDraft;
  } else if (pool.status === 'active' && pool.mode === 'season') {
    stat = (
      <div className="ph-card-stat">
        <span className="ph-card-label">{t.soloSeason}</span>
        <span className="ph-card-big">{t.seasonTeams}</span>
        <span className="ph-card-sub">{t.seasonSub}</span>
      </div>
    );
    action = t.viewStandings;
  } else if (pool.status === 'active') {
    stat = (
      <div className="ph-card-stat">
        <span className="ph-card-label">{t.yourRank}</span>
        <span className="ph-card-big">
          <span>{standing ? <>#{standing.rank}<small> / {standing.of}</small></> : '—'}</span>
          {standing && <span className="ph-card-pts"><CountUp value={standing.score} /> <small>{t.pts}</small></span>}
        </span>
      </div>
    );
    action = t.viewStandings;
  } else if (pool.status === 'lobby' && pool.mode === 'season') {
    stat = (
      <div className="ph-card-stat">
        <span className="ph-card-label">{t.soloSeason}</span>
        <span className="ph-card-big">{t.seasonTeams}</span>
        <span className="ph-card-sub">{n}/{pool.max_members} {t.seats} · {t.seasonCpu}</span>
      </div>
    );
    action = mine ? t.openLobby : t.join;
  } else if (pool.status === 'lobby') {
    stat = (
      <div className="ph-card-stat">
        <span className="ph-card-label">{t.prizePool}</span>
        <span className="ph-card-big">{formatMoney(pool.prize_pool, lang)}</span>
        <span className="ph-card-sub">{n}/{pool.max_members} {t.seats} · {t.entryFee} {formatMoney(pool.entry_fee, lang)}</span>
      </div>
    );
    action = mine ? t.openLobby : t.join;
  } else if (pool.status === 'completed') {
    stat = (
      <div className="ph-card-stat">
        <span className="ph-card-label">{t.winner}</span>
        <span className="ph-card-big">{iWon ? t.youWon : (winner?.profiles?.username || t.noWinner)}</span>
        <span className="ph-card-sub">{formatMoney(pool.prize_pool, lang)}</span>
      </div>
    );
    action = t.viewResults;
  }

  return (
    <button
      className={`ph-card status-${pool.status} ${myTurn ? 'my-turn' : ''}`}
      style={{ '--i': index }}
      onClick={() => onSelect(pool.id)}
    >
      <div className="ph-card-top">
        <span className="ph-card-logo">
          {myTeam
            ? <img src={teamLogo(myTeam.abbrev)} alt={myTeam.name} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
            : <span className="ph-card-logo-empty">?</span>}
        </span>
        <span className={`pool-status-badge pool-status-${pool.status}`}>{t[`status_${pool.status}`] || pool.status}</span>
      </div>
      <div className="ph-card-name">{pool.name}</div>
      <div className="ph-card-meta">{format}{myTeam ? ` · ${myTeam.name}` : ''}</div>
      {stat}
      <span className={`ph-card-cta ${pool.status === 'drafting' || (!mine && pool.status === 'lobby') ? 'primary' : ''}`}>{action} →</span>
    </button>
  );
}

const TR = {
  en: {
    title: 'Hockey Pools',
    create: 'Create Pool',
    myQueues: 'My Queues',
    tabMine: 'My pools',
    tabOpen: 'Open',
    tabHistory: 'History',
    formatRegular: 'Regular Season',
    formatPlayoff: 'Playoffs',
    entryFee: 'Entry',
    prizePool: 'Prize pool',
    seats: 'seats',
    loading: 'Loading…',
    emptyMine: "You're not in any pool right now.",
    emptyOpen: 'No open pools yet. Start one and invite the boys!',
    emptyHistory: 'No finished pools yet.',
    join: 'Join',
    open: 'Open',
    openLobby: 'Open lobby',
    status_lobby: 'Lobby',
    status_drafting: 'Drafting',
    status_active: 'Active',
    status_completed: 'Finished',
    onClock: 'On the clock',
    youreUp: "You're up!",
    makeYourPick: 'Make your pick',
    enterDraft: 'Enter draft',
    yourRank: 'Your rank',
    pts: 'pts',
    viewStandings: 'Standings',
    soloSeason: 'GM Season', seasonTeams: '32 teams', seasonSub: 'Best record wins', seasonCpu: 'CPU GMs fill the rest',
    viewResults: 'Results',
    winner: 'Winner',
    youWon: 'You won!',
    noWinner: 'No winner',
  },
  fr: {
    title: 'Pool de hockey',
    create: 'Créer un pool',
    myQueues: 'Mes listes',
    tabMine: 'Mes pools',
    tabOpen: 'Ouverts',
    tabHistory: 'Historique',
    formatRegular: 'Saison régulière',
    formatPlayoff: 'Séries',
    entryFee: 'Inscription',
    prizePool: 'Cagnotte',
    seats: 'places',
    loading: 'Chargement…',
    emptyMine: "Tu n'es dans aucun pool en ce moment.",
    emptyOpen: 'Aucun pool ouvert. Lancez-en un et invitez la gang!',
    emptyHistory: 'Aucun pool terminé.',
    join: 'Rejoindre',
    open: 'Ouvrir',
    openLobby: 'Ouvrir le lobby',
    status_lobby: 'Lobby',
    status_drafting: 'Repêchage',
    status_active: 'Actif',
    status_completed: 'Terminé',
    onClock: 'Au choix',
    youreUp: "C'est à toi!",
    makeYourPick: 'Fais ton choix',
    enterDraft: 'Entrer au repêchage',
    yourRank: 'Ton rang',
    pts: 'pts',
    viewStandings: 'Classement',
    soloSeason: 'Saison DG', seasonTeams: '32 équipes', seasonSub: 'La meilleure fiche gagne', seasonCpu: 'des DG CPU complètent',
    viewResults: 'Résultats',
    winner: 'Gagnant',
    youWon: 'Tu as gagné!',
    noWinner: 'Aucun gagnant',
  },
};
